import { BaseRemoteService } from '@ofqual/portal-core/core/dataAccess';
import {
  Model,
  Opts,
  PagedResponse,
  QueryParams,
  ValidatedResponse,
} from '@ofqual/portal-core/core/types';
import { paramsToSearchString } from '@ofqual/portal-core/core/utils/urlHelper';
import { PagedResponseTo } from '@ofqual/portal-core/core/utils/pagedResponseHelpers';
import {
  fromApiModel,
  SocItemModel,
  RegulatorsToNotifyModel,
} from '../domain/SocItem';
import { SocItemDetailsOfqual } from '../domain/SocItemDetailsOfqual';
import { getValidationError } from '@ofqual/portal-core/core/utils/validationHelper';
import { SocItemDetailsAo } from '../domain/SocItemDetailsAo';
import { SocEvent } from '../domain/SocEvent';

export class SocService extends BaseRemoteService {
  public async get(
    id: string,
    year: string,
    opts: Opts,
  ): Promise<SocItemModel> {
    const url = `${this.apiRoot}/v2/soc/${year}/${id}`;
    const response = await super.getRequest<Model>(url, opts);
    return fromApiModel(response);
  }

  public async search(
    queryParams: QueryParams,
    year: string,
    opts: Opts,
  ): Promise<PagedResponse<SocItemModel>> {
    const url = `${this.apiRoot}/v2/soc/${year}${paramsToSearchString(queryParams)}`;
    const response = await super.getRequest<PagedResponse<Model>>(url, opts);
    return PagedResponseTo<SocItemModel>(response, fromApiModel);
  }

  public async assign(
    id: string,
    year: string,
    upn: string,
    displayName: string,
    opts: Opts,
  ): Promise<SocItemModel> {
    const url = `${this.apiRoot}/v2/soc/${year}/${id}/assign`;
    const messageData = { id, upn, displayName };
    return await super.postRequest(url, messageData, opts);
  }

  public async unassign(
    id: string,
    year: string,
    opts: Opts,
  ): Promise<SocItemModel> {
    const url = `${this.apiRoot}/v2/soc/${year}/${id}/unassign`;
    return await super.postRequest(url, {}, opts);
  }

  public async getOqualItemDetails(
    id: string,
    year: string,
    opts: Opts,
  ): Promise<SocItemDetailsOfqual> {
    const url = `${this.apiRoot}/v2/soc/${year}/${id}/getOfqualSocDetails`;
    const response = await super.getRequest<Model>(url, opts);
    return response as SocItemDetailsOfqual;
  }

  public async putOqualItemDetails(
    year: string,
    socOfqualDetails: SocItemDetailsOfqual,
    opts: Opts,
  ): Promise<ValidatedResponse<SocItemDetailsOfqual>> {
    try {
      const url = `${this.apiRoot}/v2/soc/${year}/updateOfqualSocDetails`;
      const response = await super.putRequest(url, socOfqualDetails, opts);
      return { isValid: true, data: response as SocItemDetailsOfqual };
    } catch (err) {
      const validationError = getValidationError(err);
      if (validationError.isValidationError) {
        return { isValid: false, error: validationError.error };
      }
      throw err;
    }
  }

  public async getAoItemDetails(
    id: string,
    year: string,
    opts: Opts,
  ): Promise<SocItemDetailsAo> {
    const url = `${this.apiRoot}/v2/soc/${year}/${id}/AoSocDetails`;
    const response = await super.getRequest<Model>(url, opts);
    return response as SocItemDetailsAo;
  }

  public async putAoItemDetails(
    year: string,
    socOfqualDetails: SocItemDetailsAo,
    opts: Opts,
  ): Promise<ValidatedResponse<SocItemDetailsAo>> {
    try {
      const url = `${this.apiRoot}/v2/soc/${year}/AoSocDetails`;
      const response = await super.putRequest(url, socOfqualDetails, opts);
      return { isValid: true, data: response as SocItemDetailsAo };
    } catch (err) {
      const validationError = getValidationError(err);
      if (validationError.isValidationError) {
        return { isValid: false, error: validationError.error };
      }
      throw err;
    }
  }

  public async putRegulatorsToNotify(
    id: string,
    year: string,
    regulatorsToNotify: RegulatorsToNotifyModel,
    opts: Opts,
  ): Promise<ValidatedResponse<SocItemModel>> {
    try {
      const url = `${this.apiRoot}/v2/soc/${year}/${id}/regulatorsToNotify`;
      const response = await super.putRequest(url, regulatorsToNotify, opts);
      return { isValid: true, data: response as SocItemModel };
    } catch (err) {
      const validationError = getValidationError(err);
      if (validationError.isValidationError) {
        return { isValid: false, error: validationError.error };
      }
      throw err;
    }
  }

  public async updateActiveFileSubmission(
    year: string,
    type: string,
    socId: string,
    file: File,
    opts: Opts,
  ): Promise<unknown> {
    const url = `${this.apiRoot}/v2/soc/file/${year}/${socId}/${type}`;
    return await super.postRequestFormData(url, { file }, opts);
  }

  public async downloadSocFiles(
    year: string,
    socItemFileId: string,
    socId: string,
    opts: Opts,
  ): Promise<Blob> {
    const url = `${this.apiRoot}/v2/soc/file/${year}/${socId}/${socItemFileId}`;
    return await super.getRequestBlob(url, opts);
  }

  public async getSocTemplate(
    year: string,
    type: string,
    opts: Opts,
  ): Promise<Blob> {
    const url = `${this.apiRoot}/v2/soc/file/${year}/${type}/template`;
    return await super.getRequestBlob(url, opts);
  }

  public async addCaseNote(
    id: string,
    files: File[],
    note: string,
    noteType: number,
    opts: Opts,
  ): Promise<unknown> {
    const url = `${this.apiRoot}/v2/soc/activity/${id}/casenote`;
    return await super.postRequestFormData(
      url,
      { note, files, noteType },
      opts,
    );
  }
  public async sendMessage(
    id: string,
    files: File[],
    message: string,
    opts: Opts,
  ) {
    const url = `${this.apiRoot}/v2/soc/activity/${id}/message`;
    return await super.postRequestFormData(url, { message, files }, opts);
  }

  public async downloadActivityAttachment(
    id: string,
    activityId: string,
    blobId: string,
    opts: Opts,
  ): Promise<Blob> {
    const url = `${this.apiRoot}/v2/soc/activity/${id}/documents/${activityId}/download/${blobId}`;
    return await super.getRequestBlob(url, opts);
  }

  public async addEvent(id: string, event: SocEvent, opts: Opts) {
    const url = `${this.apiRoot}/v2/soc/activity/${id}/event`;
    return await super.postRequest<SocEvent>(url, event, opts);
  }

  public async completeEvent<SocEventComplete>(
    id: string,
    eventId: string,
    completeEvent: SocEventComplete,
    opts: Opts,
  ) {
    const url = `${this.apiRoot}/v2/soc/activity/${id}/event/${eventId}/complete`;
    return await super.postRequestFormData(url, completeEvent, opts);
  }

  public async sendInternalMessage(
    id: string,
    files: File[],
    subject: string,
    message: string,
    recipients: string[],
    opts: Opts,
  ): Promise<unknown> {
    const url = `${this.apiRoot}/v2/soc/activity/${id}/internal-message`;
    const messageData = { id, subject, message, recipients };
    const messageSerialized = JSON.stringify(messageData);
    return await super.postRequestFormData(
      url,
      { messageSerialized, files },
      opts,
    );
 }

  public async enableResubmit(
    id: string,
    year: string,
    opts: Opts,
  ): Promise<SocItemModel> {
    const url = `${this.apiRoot}/v2/soc/${year}/${id}/resubmit`;
    return await super.postRequest(url, {}, opts);
  }

  public async SubmitSoc(
    socId: string,
    year: string,
    opts: Opts
  ): Promise < unknown > {
    try {
      const url = `${this.apiRoot}/v2/soc/${year}/${socId}/submit`;
      const response = await super.postRequest(url, {}, opts);
      return { isValid: true, data: response as SocItemDetailsAo };
    }
    catch(err) {
      const validationError = getValidationError(err);
      if (validationError.isValidationError) {
        return { isValid: false, error: validationError.error };
      }
      throw err;
    }
  }

  public async close(
    id: string,
    year: string,
    closeReason: string,
    opts: Opts,
  ): Promise<ValidatedResponse<SocItemModel>> {
    const url = `${this.apiRoot}/v2/soc/${year}/${id}/close`;
    try {
      const response: Model = await this.postRequest<SocItemModel>(url, closeReason, opts);
      return {
        isValid: true,
        data: fromApiModel(response),
      };
    } catch (err) {
      const validationError = getValidationError(err);
      if (validationError.isValidationError) {
        return { isValid: false, error: validationError.error };
      }
      throw err;
    }
  }

  public async reopen(
    id: string,
    year: string,
    opts: Opts,
  ): Promise<ValidatedResponse<SocItemModel>> {
    const url = `${this.apiRoot}/v2/soc/${year}/${id}/reopen`;
    try {
      const response: Model = await this.postRequest<SocItemModel>(url, {}, opts);
      return {
        isValid: true,
        data: fromApiModel(response),
      };
    } catch (err) {
      const validationError = getValidationError(err);
      if (validationError.isValidationError) {
        return { isValid: false, error: validationError.error };
      }
      throw err;
    }
  }

}
